import { type CurrencyCode, systemClock, todayUtc } from '@taler/domain'
import { type ComputedRef, computed } from 'vue'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'
import type { ReportFlowsOptions } from './use-report-flows.ts'
import { useReportRangeQueryState, type ReportRangeQueryState } from './use-report-range-query-state.ts'

export interface ReportScope {
  readonly session: ReturnType<typeof useSession>
  readonly state: ReportRangeQueryState
  /** Options shared by every flow read on a page, enabled only while the page may query. */
  flows(gate?: () => boolean): ReportFlowsOptions
  readonly canQuery: ComputedRef<boolean>
  readonly displayCurrency: ComputedRef<CurrencyCode>
}

/** The session, window and display currency every Reports tab reads from, in one place. */
export function useReportScope(canView: () => boolean): ReportScope {
  const session = useSession()
  const { displayCurrency } = useDisplayCurrency()
  const state = useReportRangeQueryState(() => todayUtc(systemClock))
  const canQuery = computed(
    () =>
      session.activeLedger.value !== undefined &&
      canView() &&
      !state.isInverted.value &&
      !state.isTooLong.value,
  )

  return {
    session,
    state,
    canQuery,
    displayCurrency,
    flows: (gate = () => true) => ({
      ledgerId: () => session.activeLedger.value?.id,
      range: () => state.range.value,
      displayCurrency: () => displayCurrency.value,
      enabled: () => canQuery.value && gate(),
    }),
  }
}
